<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3 pb-4">
      <SimplePageHeader title="QuickBooks Connections" class="px-4 pr-5 mb-3" />
      <v-alert v-if="configurationError" type="error" outlined dense class="mx-4">
        {{ configurationError }}
      </v-alert>
      <v-alert
        v-if="outcomeAlert"
        :type="outcomeAlert.type"
        dismissible
        dense
        class="mx-4"
        @input="outcomeAlert = null"
      >
        {{ outcomeAlert.text }}
      </v-alert>
      <v-data-table
        :headers="headers"
        :items="connections"
        item-key="businessEntityId"
        hide-default-footer
        disable-pagination
        class="px-4"
      >
        <template v-slot:item.companyName="{ item }">
          {{ item.companyName || 'Not connected yet' }}
        </template>
        <template v-slot:item.statusName="{ item }">
          <v-chip
            x-small
            label
            :color="statusAttrs(item.statusName).color"
            :dark="statusAttrs(item.statusName).dark"
          >{{ item.statusName }}</v-chip>
        </template>
        <template v-slot:item.lastRenewedAt="{ item }">
          {{ item.lastRenewedAt | formatDate }}
        </template>
        <template v-slot:item.expenseItemId="{ item }">
          <v-autocomplete
            :key="`expense-item-${item.businessEntityId}-${expenseItemResets[item.businessEntityId] || 0}`"
            :value="item.expenseItemId"
            :items="expenseItemOptions(item)"
            item-text="name"
            item-value="id"
            placeholder="Choose the expense Item"
            dense
            clearable
            :hide-details="!expenseItemErrors[item.businessEntityId]"
            :error-messages="expenseItemErrors[item.businessEntityId]"
            :disabled="!isConnected(item)"
            @change="handleExpenseItemChange(item, $event)"
          />
        </template>
        <template v-slot:item.notificationRecipients="{ item }">
          <div class="d-flex align-center">
            <v-text-field
              :value="recipientValue(item)"
              placeholder="email:Name, email2:Name2"
              dense
              hide-details
              @input="editRecipients(item, $event)"
            />
            <v-btn
              small
              text
              color="primary"
              :disabled="!isRecipientEdited(item)"
              @click="saveRecipients(item)"
            >Save</v-btn>
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="!isConnected(item)"
            small
            color="primary"
            :disabled="!!configurationError"
            @click="handleConnect(item)"
          >Connect</v-btn>
          <v-btn
            v-else
            small
            outlined
            color="error"
            @click="handleDisconnect(item)"
          >Disconnect</v-btn>
        </template>
      </v-data-table>
    </div>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { SimplePageHeader } from '@/components/pageComponents'
import { connectOutcomeAlert, connectionStatusAttrs, isConnected, withStoredOption } from './quickBooksConnectionHelpers'

export default {
  components: {
    SimplePageHeader
  },
  data: () => ({
    outcomeAlert: null,
    recipientEdits: {},
    expenseItems: {},
    expenseItemErrors: {},
    expenseItemResets: {},
    headers: [
      { text: 'Business Entity', value: 'businessEntityName', sortable: false },
      { text: 'QuickBooks Company', value: 'companyName', sortable: false },
      { text: 'Status', value: 'statusName', sortable: false },
      { text: 'Last Renewed', value: 'lastRenewedAt', sortable: false },
      { text: 'Expense Item', value: 'expenseItemId', sortable: false, width: '20%' },
      { text: 'Notification Recipients', value: 'notificationRecipients', sortable: false, width: '35%' },
      { text: '', value: 'actions', sortable: false, align: 'end' }
    ]
  }),
  computed: {
    ...mapFields('quickBooksConnections', ['connections', 'configurationError'])
  },
  created () {
    this.showOutcomeFromQuery()
    this.getConnections().then(() => this.loadExpenseItems())
  },
  methods: {
    ...mapActions('quickBooksConnections', ['getConnections', 'connect', 'disconnect', 'updateNotificationRecipients', 'listItems', 'setExpenseItem']),
    statusAttrs: connectionStatusAttrs,
    isConnected,
    showOutcomeFromQuery () {
      this.outcomeAlert = connectOutcomeAlert(this.$route.query)
      if (this.outcomeAlert) {
        this.$router.replace({ query: {} })
      }
    },
    navigateTo (url) {
      window.location.href = url
    },
    confirm (message) {
      return window.confirm(message)
    },
    handleConnect (connection) {
      return this.connect({ businessEntityId: connection.businessEntityId })
        .then(url => this.navigateTo(url))
        .catch(() => {})
    },
    handleDisconnect (connection) {
      const company = connection.companyName || 'QuickBooks'
      if (!this.confirm(`Disconnect ${company} from ${connection.businessEntityName}? StaffLynx will stop posting to it until it is connected again.`)) {
        return Promise.resolve()
      }

      return this.disconnect({ businessEntityId: connection.businessEntityId })
        .then(result => {
          this.outcomeAlert = result.revokedAtIntuit
            ? { type: 'success', text: `${company} disconnected.` }
            : { type: 'warning', text: result.message }
        })
        .catch(() => {})
    },
    recipientValue (connection) {
      return this.isRecipientEdited(connection)
        ? this.recipientEdits[connection.businessEntityId]
        : connection.notificationRecipients
    },
    isRecipientEdited (connection) {
      return Object.prototype.hasOwnProperty.call(this.recipientEdits, connection.businessEntityId)
    },
    editRecipients (connection, value) {
      this.recipientEdits = { ...this.recipientEdits, [connection.businessEntityId]: value }
    },
    loadExpenseItems () {
      const connected = this.connections.filter(isConnected)
      return Promise.all(connected.map(({ businessEntityId }) =>
        this.listItems({ businessEntityId })
          .then(items => {
            this.expenseItems = { ...this.expenseItems, [businessEntityId]: items }
          })
          .catch(error => {
            this.expenseItemErrors = { ...this.expenseItemErrors, [businessEntityId]: error.message }
          })
      ))
    },
    expenseItemOptions (connection) {
      const items = this.expenseItems[connection.businessEntityId]
      if (!items) return []
      return isConnected(connection) && !this.expenseItemErrors[connection.businessEntityId]
        ? withStoredOption(items, connection.expenseItemId, 'name')
        : items
    },
    handleExpenseItemChange (connection, expenseItemId) {
      const businessEntityId = connection.businessEntityId
      return this.setExpenseItem({ businessEntityId, expenseItemId: expenseItemId || '' })
        .catch(() => {
          // Re-mounting the dropdown puts back the stored Item it was showing before the failed save.
          this.expenseItemResets = { ...this.expenseItemResets, [businessEntityId]: (this.expenseItemResets[businessEntityId] || 0) + 1 }
        })
    },
    saveRecipients (connection) {
      const businessEntityId = connection.businessEntityId
      return this.updateNotificationRecipients({
        businessEntityId,
        notificationRecipients: this.recipientEdits[businessEntityId]
      })
        .then(() => {
          const remaining = { ...this.recipientEdits }
          delete remaining[businessEntityId]
          this.recipientEdits = remaining
        })
        .catch(() => {})
    }
  }
}
</script>
